import React, { useState, useEffect, useRef, useCallback } from "react";
import { FaWeightHanging } from "react-icons/fa";
import { Row, Col, Card, CardTitle, CardBody, CardFooter } from "reactstrap";

import api from "../../../../../services/api";
import { formatCurrency, formatCurrencyInteiro } from "../../../../../utils/formatCurrency";
import { useCompanies } from "../../../../../hooks/companies";
import "../../indicadoresLinha.css";

// PO1-2712 (revisão pós-QA) — mesmo formato do ChartTotalizadores/SavingsGoBID
const fmtPctSigned = (v) => {
  if (v === null || v === undefined || !Number.isFinite(v)) return "-";
  return `${v > 0 ? "+" : ""}${v.toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
};

const fmtPercent = (v) => {
  if (v === null || v === undefined || !Number.isFinite(v)) return "-";
  return `${v.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
};

export default function IndicadoresFrete({ deferLoad = false, onRegisterFetch }) {
  const [indicadores, setIndicadores] = useState([]);
  const { empresa, transportadora, arrayParams } = useCompanies();
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const loadIndicadores = useCallback(async () => {
    try {
      const params = {};
      if (arrayParams.embarcadores) params.embarcadores = arrayParams.embarcadores;
      if (arrayParams.transportadoras) params.transportadoras = arrayParams.transportadoras;

      const [freightRes, entregaRes, mensalRes] = await Promise.all([
        api.get("/dashboard/autopilot/freight-indicators", { params }),
        api.get("/dashboard/custo/frete-entrega-indicador", { params }),
        api.get("/dashboard/custo/media-mensal", { params }),
      ]);

      const arrayT = [];

      if (freightRes.status === 200) {
        const byKey = (key) =>
          (freightRes.data.indicators || []).find((i) => i && i.key === key);

        const freteKg = byKey("frete_kg");
        arrayT.push({
          titulo: "Frete/Kg",
          subtitulo: "R$ por kg embarcado (30 dias)",
          // changePct vem como fração (0.05 = +5%); convertido pra pontos percentuais
          variacao: freteKg && freteKg.changePct !== null ? freteKg.changePct * 100 : null,
          valor: freteKg && freteKg.value !== null ? formatCurrency(freteKg.value) : "-",
          icon: (
            <div className="info-icon text-center icon-warning">
              <i className="fas weight-hanging">
                <FaWeightHanging icon="weight-hanging" />
              </i>
            </div>
          ),
        });

        const freteNf = byKey("frete_nf");
        arrayT.push({
          titulo: "Frete/Valor NF",
          subtitulo: "% do frete sobre valor da NF (30 dias)",
          variacao: freteNf && freteNf.changePct !== null ? freteNf.changePct * 100 : null,
          valor: freteNf && freteNf.value !== null ? fmtPercent(freteNf.value) : "-",
          icon: (
            <div className="info-icon text-center icon-danger">
              <i className="tim-icons icon-chart-bar-32" />
            </div>
          ),
        });
      }

      if (entregaRes.status === 200) {
        const { value, changePct } = entregaRes.data;
        arrayT.push({
          titulo: "Frete/Entrega",
          subtitulo: "Frete médio por CT-e (30 dias)",
          variacao: changePct !== null && changePct !== undefined ? changePct * 100 : null,
          valor: value !== null && value !== undefined ? formatCurrencyInteiro(value) : "-",
          icon: (
            <div className="info-icon text-center icon-primary">
              <i className="tim-icons icon-delivery-fast" />
            </div>
          ),
        });
      }

      if (mensalRes.status === 200) {
        const variacaoObj = mensalRes.data.variacaoVsPeriodoAnterior;
        const variacao = variacaoObj ? Number(variacaoObj.mensalFrete) : null;
        arrayT.push({
          titulo: "Frete/Mês",
          subtitulo: "Custo total de frete (média 3 meses)",
          variacao: Number.isFinite(variacao) ? variacao : null,
          valor:
            mensalRes.data.mensalFrete !== null && mensalRes.data.mensalFrete !== undefined
              ? formatCurrencyInteiro(mensalRes.data.mensalFrete)
              : "-",
          icon: (
            <div className="info-icon text-center icon-success">
              <i className="tim-icons icon-money-coins" />
            </div>
          ),
        });
      }

      if (isMounted.current) {
        setIndicadores(arrayT);
      }
    } catch (error) {
      if (isMounted.current) {
        console.error("Erro ao carregar indicadores de frete:", error);
        setIndicadores([]);
      }
    }
  }, [arrayParams.embarcadores, arrayParams.transportadoras]);

  useEffect(() => {
    if (onRegisterFetch) {
      onRegisterFetch(loadIndicadores);
    }

    if (!deferLoad) {
      loadIndicadores();
    }
  }, [deferLoad, empresa, loadIndicadores, onRegisterFetch, transportadora]);

  return (
    <Row className="indicadores-linha">
      {indicadores.map((indicador, index) => (
        <Col lg="3" md="6" key={index.toString()}>
          <Card
            style={{ boxShadow: "0px 1px 20px 0px rgb(0, 0, 0)" }}
            className="card-stats"
          >
            <CardBody>
              <Row>
                <Col xs="4">{indicador.icon}</Col>
                <Col xs="8">
                  <div className="numbers">
                    <CardTitle tag="h4">{indicador.titulo}</CardTitle>
                    <CardTitle tag="h3">{indicador.valor}</CardTitle>
                  </div>
                </Col>
              </Row>
            </CardBody>
            <CardFooter>
              <hr />
              <div
                className="stats"
                style={{ display: "flex", alignItems: "center", gap: 6 }}
              >
                <i className="tim-icons icon-sound-wave" />
                {indicador.subtitulo}
                {indicador.variacao !== null && (
                  <span
                    title="Variação em relação ao período anterior equivalente"
                    style={{
                      marginLeft: "auto",
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      whiteSpace: "nowrap",
                      color:
                        indicador.variacao > 0
                          ? "var(--everlog-status-danger)"
                          : indicador.variacao < 0
                          ? "var(--everlog-status-success)"
                          : "var(--everlog-card-muted)",
                    }}
                  >
                    {indicador.variacao > 0 ? "↑" : indicador.variacao < 0 ? "↓" : ""}{" "}
                    {fmtPctSigned(indicador.variacao)}
                  </span>
                )}
              </div>
            </CardFooter>
          </Card>
        </Col>
      ))}
    </Row>
  );
}
